import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { ScissorsLineDashed, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DialogUploadPostContentProps, PostUploadMediaType, UploadPostContentType } from "utils/enum/UploadPostContentType";
import useDialogUploadPostStore from "store/dialog/useDialogUploadPostContentStore";
import { useEffect, useState } from "react";
import FileUpload from "./components/FileUpload";
import { InputDescriptionStaticHeight } from "components/app/flipflop/input-description-static-height/InputDescriptionStaticHeight";
import { DialogTrimVideo } from "./DialogTrimVideo";
import { DialogViewVideo } from "./DialogViewVideo";
import UploadSetting from "./components/UploadSetting";
import Loader2 from "components/app/loader/Loader2";
import appStore from "store/appStore";
import { DialogViewImage } from "./DialogViewImage";
import { DialogMusic } from "./DialogMusic";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import FlvLiveView from "./components/FlvLiveView";
import { Service } from "utils/services";
import { Link, useNavigate } from "react-router-dom";

interface DialogProps {
  setIsDialogOpen: (value: boolean) => void;
  dialogUploadPostContent: DialogUploadPostContentProps;
}

export function DialogUploadPostContent({ setIsDialogOpen, dialogUploadPostContent }: DialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { showDialogConfirm } = appStore();

  const {
    postContentType,
    uploadStep,
    userPost,
    initializingFile,
    mediaType,
    showLoader,
    textLoader,
    showError,
    textError,
    initializePage,
    disposePage,
    onTryAgain,

    // * --- Video --- * //
    videoFileForTrim,
    videoUrl,
    videoThumbnails,
    selectedVideoThumbnail,
    onVideoUploaded,
    onVideoTrimmed,
    setVideoFileForTrim,
    setSelectedVideoThumbnail,
    setVideoFileToTrim,

    onChangeText,

    // * --- Photos --- * //
    viewPhotos,
    onPhotosUploaded,

    // * --- Music --- * //
    setMusic,

    // * --- Live --- * //
    streamingHostCluster,
    streamingKey,
    isGettingStreamingKey,
    isStreaming,
    postLive,
    getStreamingKey,

    // * --- Posting --- * //
    onPostSubmit,
  } = useDialogUploadPostStore();

  const [dialogViewVideo, setDialogViewVideo] = useState<{
    isOpen: boolean;
    videoUrl: string | null;
  }>({
    isOpen: false,
    videoUrl: null,
  });

  const [dialogViewImage, setDialogViewImage] = useState<{
    isOpen: boolean;
    image: string | null;
  }>({
    isOpen: false,
    image: null,
  });

  const [dialogMusic, setDialogMusic] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  const handleDiscardPost = async () => {
    try {
      if (uploadStep === "Prepare") {
        showDialogConfirm({
          title: "Discard",
          message: "Do you want to discard post ?",
          onSubmit: (_) => setIsDialogOpen(false),
        });
      } else {
        setIsDialogOpen(false);

        // onOpenChange={(open) =>handleDiscardPost(open)}
      }
    } catch (err) {
      console.error("[error] Trimming failed:", err);
    }
  };

  useEffect(() => {
    initializePage(dialogUploadPostContent.type);

    return () => {
      disposePage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _errorContent = (text: string) => {
    return (
      <div className="absolute inset-0 z-50 bg-background flex items-center justify-center rounded-xl">
        <div className="flex flex-col items-center space-y-4 ">
          <p className="text-base font-normal text-center">{text}</p>
          <Button onClick={onTryAgain}>Try Again</Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={dialogUploadPostContent.isOpen}>
      <DialogContent
        isShowBtnXClose={false}
        className="max-w-full lg:max-w-[700px]  h-[calc(100vh-80px)] py-2 px-0 flex flex-col "
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        {showLoader && <Loader2 text={textLoader} />}

        {showError && _errorContent(textError)}

        <DialogHeader>
          <DialogTitle className="mt-2 text-center ">{t(`${postContentType === UploadPostContentType.liveVideo ? "Create Live Video" : "Create Post"}`)}</DialogTitle>

          <div
            className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
            onClick={() => handleDiscardPost()}
          >
            <X />
          </div>
        </DialogHeader>

        {postContentType === UploadPostContentType.status || postContentType === UploadPostContentType.liveVideo ? (
          postLive ? (
            <div className="w-full h-full flex items-center justify-center ">
              <div className="px-4 space-y-4 text-center">
                <p className="mb-2 text-sm font-semibold">Your streaming is started!</p>
                <Link to={`/live-studio`}>
                  <Button onClick={() => setIsDialogOpen(false)}>Go To Live Studio</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="w-full overflow-x-auto flex-1 items-center" style={{ scrollbarGutter: "stable" }}>
              {!streamingKey ? (
                <></>
              ) : (
                <div className="px-4 space-y-4">
                  <div className="flex flex-row aspect-[16/9] w-full space-x-2 justify-between ">
                    <div className="flex flex-grow flex-col space-y-2 ">
                      <InputDescriptionStaticHeight onChangeText={onChangeText} />
                    </div>

                    <FlvLiveView />
                  </div>

                  {!isStreaming && (
                    <Card className="flex-1">
                      <CardHeader>
                        <CardTitle className="text-lg">Streaming software setup</CardTitle>
                        <p className="text-sm text-zinc-400">Copy and paste the stream key into your streaming software.</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-4">
                          <p className="mb-2 text-sm font-semibold">Stream Server</p>
                          <div className="flex items-center space-x-2">
                            <Input value={`${streamingHostCluster.stream_host_name}/live`} readOnly className="flex-1 " />
                            <Button
                              onClick={() => {
                                Service.copyClipboard(`${streamingHostCluster.stream_host_name}/live`);
                              }}
                            >
                              Copy
                            </Button>
                          </div>

                          <p className="mb-2 text-sm font-semibold">Stream key</p>
                          <div className="flex items-center space-x-2">
                            <Input value={isGettingStreamingKey ? "Loading..." : !streamingKey ? "Your streaming is started!" : streamingKey} readOnly className="flex-1 " />
                            <Button
                              onClick={() => {
                                if (streamingKey) Service.copyClipboard(streamingKey);
                              }}
                              disabled={isGettingStreamingKey || !streamingKey}
                            >
                              Copy
                            </Button>
                            <Button variant="secondary" onClick={getStreamingKey} disabled={isGettingStreamingKey || !streamingKey}>
                              Reset
                            </Button>
                          </div>
                          <p className="mt-2 text-xs text-zinc-400">Once you start to preview the broadcast you have up to 4 hours to go live.</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <UploadSetting
                    onAddMusic={() => {
                      setDialogMusic({
                        isOpen: true,
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )
        ) : uploadStep === "Attachments" ? (
          <div className="w-full h-full flex-grow relative px-4">
            {/* <UploadSetting
              onAddMusic={() => {
                setDialogMusic({
                  isOpen: true,
                });
              }}
            /> */}
            {initializingFile ? <></> : <FileUpload onVideoUpload={onVideoUploaded} onPhotosUpload={onPhotosUploaded} />}
          </div>
        ) : (
          <div className="w-full overflow-x-auto flex-1 items-center" style={{ scrollbarGutter: "stable" }}>
            <div className="px-4 space-y-4">
              <>
                {mediaType === PostUploadMediaType.video ? (
                  <div className="flex flex-row aspect-[16/9] w-full space-x-2 justify-between ">
                    <div className="flex flex-grow flex-col space-y-2 ">
                      <InputDescriptionStaticHeight onChangeText={onChangeText} />
                      <div className="flex w-full ">
                        {videoThumbnails && (
                          <div className="grid grid-cols-5 gap-3">
                            {videoThumbnails.map((src, i) => (
                              <button
                                key={i}
                                onClick={() => setSelectedVideoThumbnail(src)}
                                className={`aspect-square w-full border-1 rounded-md overflow-hidden ${
                                  selectedVideoThumbnail === src ? "border-primary ring-4" : "border-transparent"
                                }`}
                              >
                                <img src={src} alt={`Thumbnail ${i}`} className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedVideoThumbnail && (
                      <div
                        className="relative aspect-[9/16]  h-full rounded-xl flex items-center justify-center overflow-hidden shrink-0"
                        onClick={() =>
                          setDialogViewVideo({
                            isOpen: true,
                            videoUrl: videoUrl,
                          })
                        }
                      >
                        <div
                          className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl"
                          style={{
                            backgroundImage: `url("${selectedVideoThumbnail}")`,
                          }}
                        />

                        <img src={selectedVideoThumbnail} alt="Thumbnail" className="relative z-10 w-full h-auto object-contain" loading="lazy" />

                        <div className="absolute top-1 right-1 flex flex-col space-y-2 z-20">
                          <Button
                            variant="secondary"
                            size="icon"
                            className=" pointer-events-auto w-8 h-8 rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVideoFileToTrim();
                            }}
                          >
                            <ScissorsLineDashed size={16} />
                          </Button>

                          {/* <Button
                         variant="secondary"
                         size="icon"
                         className=" pointer-events-auto w-8 h-8 rounded-full"
                         onClick={(e) => {
                           e.stopPropagation();
                           setVideoFileToTrim();
                         }}
                       >
                         <Settings2 size={16} />
                       </Button> */}
                        </div>
                      </div>
                    )}
                  </div>
                ) : viewPhotos.length === 1 ? (
                  <div className="flex flex-row aspect-[16/9] w-full space-x-2 justify-between ">
                    <div className="flex flex-grow flex-col space-y-2 ">
                      <InputDescriptionStaticHeight onChangeText={onChangeText} />
                    </div>

                    <div className="relative aspect-[9/16]  h-full rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                      <div
                        className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl"
                        style={{
                          backgroundImage: `url("${viewPhotos[0]}")`,
                        }}
                      />

                      <img src={viewPhotos[0]} alt="Thumbnail" className="relative z-10 w-full h-auto object-contain" loading="lazy" />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-row w-full aspect-[16/9] space-x-2 justify-between">
                    <div className="flex flex-grow flex-col space-y-2 overflow-hidden">
                      <InputDescriptionStaticHeight onChangeText={onChangeText} />

                      <div className="w-full overflow-x-auto scroll-smooth">
                        <div className="inline-flex gap-3 px-1 min-w-fit">
                          {viewPhotos.map((src, i) => (
                            <div
                              key={i}
                              className="aspect-square w-28 flex-shrink-0 border rounded-md overflow-hidden"
                              onClick={() => setDialogViewImage({ isOpen: true, image: src })}
                            >
                              <img src={src} alt={`Thumbnail ${i}`} className="w-full h-full object-cover rounded-md" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <UploadSetting
                  onAddMusic={() => {
                    setDialogMusic({
                      isOpen: true,
                    });
                  }}
                />
              </>
            </div>
          </div>
        )}

        <DialogFooter className="p-4">
          {postContentType === UploadPostContentType.status || postContentType === UploadPostContentType.liveVideo ? (
            <></>
          ) : (
            <Button onClick={() => onPostSubmit(setIsDialogOpen, navigate)} disabled={uploadStep === "Attachments"}>
              {t("Post")}
            </Button>
          )}

          {postContentType === UploadPostContentType.liveVideo && !postLive && (
            <Button onClick={() => onPostSubmit(setIsDialogOpen, navigate)} disabled={!isStreaming}>
              {t("Post")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {videoFileForTrim && (
        <DialogTrimVideo
          setIsDialogOpen={(isOpen) => {
            setVideoFileForTrim(null);
          }}
          file={videoFileForTrim}
          userPost={userPost}
          isOpen={!!videoFileForTrim}
          isStudio={videoUrl ? true : false}
          onCompleted={onVideoTrimmed}
        />
      )}

      {dialogViewVideo.isOpen && (
        <DialogViewVideo
          setIsDialogOpen={() => {
            setDialogViewVideo({
              isOpen: false,
              videoUrl: null,
            });
          }}
          videoUrl={dialogViewVideo.videoUrl!}
          isOpen={dialogViewVideo.isOpen}
        />
      )}

      {dialogViewImage.isOpen && (
        <DialogViewImage
          setIsDialogOpen={() => {
            setDialogViewImage({
              isOpen: false,
              image: null,
            });
          }}
          image={dialogViewImage.image!}
          isOpen={dialogViewImage.isOpen}
        />
      )}

      {dialogMusic.isOpen && (
        <DialogMusic
          setIsDialogOpen={() => {
            setDialogMusic({
              isOpen: false,
            });
          }}
          isOpen={dialogMusic.isOpen}
          onSelectedMusic={(music) => {
            setDialogMusic({
              isOpen: false,
            });
            setMusic(music);
          }}
        />
      )}
    </Dialog>
  );
}

